import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import api from "@/lib/api";

/**
 * Inline "Didn't get the verification email? Resend" widget.
 * - Available on the register-success card AND on the sign-in page (after a
 *   verification-blocked login attempt or via the always-visible link).
 * - Wraps POST /api/collector-auth/resend-verification which is already
 *   rate-limited AND anti-enumeration-safe on the backend — we only ever
 *   surface the generic success message.
 */
export default function ResendVerificationInline({
  email: initialEmail = "",
  testidPrefix = "resend",
}) {
  const [open, setOpen] = useState(Boolean(initialEmail));
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // Sync a parent-driven `initialEmail` prop change into local state so that
  // (a) after a verification-blocked login attempt the parent can flip us
  // open with the email pre-filled, and (b) if the user retries after a
  // previous "sent" success we reset back to the form. Ignore empty-string
  // updates so the parent doesn't force the widget shut inadvertently.
  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setOpen(true);
      setSent(false);
    }
  }, [initialEmail]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!clean || !/^\S+@\S+\.\S+$/.test(clean)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/collector-auth/resend-verification", { email: clean });
      // Backend deliberately returns the same message regardless of whether
      // the account exists — respect that and show it verbatim.
      const msg = r.data?.message ||
        "If this email has a pending verification, we've sent a new link.";
      setSent(true);
      toast.success(msg);
    } catch (err) {
      // 429 = rate-limited (either IP or email bucket exceeded).
      if (err.response?.status === 429) {
        toast.error("Too many attempts — please wait a few minutes and try again.");
      } else {
        // Never surface anything that could leak account existence.
        toast.error("Could not resend right now. Please try again shortly.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-xl border-2 border-[var(--panini-yellow)] bg-[var(--panini-yellow-soft)] px-4 py-3 text-sm text-[var(--ink)]"
        data-testid={`${testidPrefix}-resend-sent`}
      >
        <div className="font-bold uppercase tracking-widest text-[10px] text-[var(--ink)]">Request received</div>
        <p className="mt-1">
          If this email has a pending verification, a new link is on its way.
          It may take a few minutes to arrive — check your spam folder too.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm text-[var(--smoke)] hover:text-[var(--ink)] underline text-left"
        data-testid={`${testidPrefix}-resend-open`}
      >
        Didn&apos;t receive the verification email? Resend
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border-2 border-[var(--line)] bg-white p-4 space-y-3"
      data-testid={`${testidPrefix}-resend-form`}
      noValidate
    >
      <div className="flex items-center gap-2 text-sm font-bold">
        <Mail size={14} /> Resend verification email
      </div>
      <label className="block">
        <div className="kicker kicker-black text-[10px] mb-1">Email address</div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="gm-field"
          data-testid={`${testidPrefix}-resend-email`}
          placeholder="you@example.com"
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="btn-red text-sm py-2 px-4 disabled:opacity-60"
          data-testid={`${testidPrefix}-resend-submit`}
        >
          {busy ? "Sending…" : "Send new link"}
        </button>
        {!initialEmail && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs text-[var(--smoke)] hover:text-[var(--ink)]"
            data-testid={`${testidPrefix}-resend-cancel`}
          >
            Cancel
          </button>
        )}
      </div>
      <p className="text-[11px] text-[var(--smoke)]">
        For security, we&apos;ll show the same message whether or not this
        email is registered.
      </p>
    </form>
  );
}
